import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

interface Stats {
  totalEmployees: number;
  pendingCandidates: number;
  pendingLeaveApplications: number;
  pendingClaims: number;
}

const QUICK_ACTIONS = [
  { label: 'Add Employee', path: '/hr/employees' },
  { label: 'Apply Leave', path: '/lms/leave-applications' },
  { label: 'Submit Claim', path: '/ecs/claims' },
  { label: 'View Payslips', path: '/payroll/payslips' },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const storedUser = localStorage.getItem('user');
  const user = storedUser ? JSON.parse(storedUser) : null;

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const [employeesRes, candidatesRes, leaveRes, claimsRes] = await Promise.allSettled([
        api.get('/employees'),
        api.get('/hr/candidates'),
        api.get('/lms/leave-applications'),
        api.get('/ecs/claims'),
      ]);

      const employees =
        employeesRes.status === 'fulfilled' ? employeesRes.value.data.employees || [] : [];
      const candidates =
        candidatesRes.status === 'fulfilled' ? candidatesRes.value.data.candidates || [] : [];
      const leaveApplications =
        leaveRes.status === 'fulfilled' ? leaveRes.value.data.applications || [] : [];
      const claims = claimsRes.status === 'fulfilled' ? claimsRes.value.data.claims || [] : [];

      setStats({
        totalEmployees: employees.length,
        pendingCandidates: candidates.filter((c: any) => c.status === 'new').length,
        pendingLeaveApplications: leaveApplications.filter((l: any) => l.status === 'APPLIED')
          .length,
        pendingClaims: claims.filter((c: any) => c.status === 'SUBMITTED').length,
      });
    } finally {
      setLoading(false);
    }
  };

  const tiles = [
    { label: 'Total Employees', value: stats?.totalEmployees, color: 'border-blue-500' },
    { label: 'Pending Candidates', value: stats?.pendingCandidates, color: 'border-amber-500' },
    {
      label: 'Pending Leave Applications',
      value: stats?.pendingLeaveApplications,
      color: 'border-purple-500',
    },
    { label: 'Pending Claims', value: stats?.pendingClaims, color: 'border-red-500' },
  ];

  return (
    <div className="p-6 space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">
          Welcome back{user?.email ? `, ${user.email}` : ''}
        </h1>
        <p className="text-gray-500 text-sm mt-1">Here's what's happening across MAVIS today.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {tiles.map((tile) => (
          <div
            key={tile.label}
            className={`bg-white rounded-lg shadow p-6 border-l-4 ${tile.color}`}
          >
            <p className="text-sm text-gray-500">{tile.label}</p>
            <p className="text-3xl font-bold text-gray-800 mt-2">
              {loading ? '—' : tile.value ?? 0}
            </p>
          </div>
        ))}
      </div>

      <div>
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {QUICK_ACTIONS.map((action) => (
            <button
              key={action.path}
              onClick={() => navigate(action.path)}
              className="bg-white rounded-lg shadow p-4 text-left hover:shadow-md transition"
            >
              <span className="text-sm font-medium text-blue-600">{action.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
